import { useMutation, gql } from "@apollo/client";
import { useSnackbar } from "notistack";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { GET_MEMBERSHIP_DETAILS_QUERY } from "../../person/queries";

const DELETE_MEMBER_BAND_MUTATION = gql`
  mutation DeleteMemberBand($id: ID!) {
    deleteMemberBand(id: $id) {
      success
      message
    }
  }
`;

interface DeleteMembershipModalProps {
  open: boolean;
  onClose: () => void;
  bandId: string;
  pgaId: string;
}

export default function DeleteMembershipModal({ open, onClose, bandId, pgaId }: DeleteMembershipModalProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [deleteMemberBand, { loading }] = useMutation(DELETE_MEMBER_BAND_MUTATION, {
    refetchQueries: [
      {
        query: GET_MEMBERSHIP_DETAILS_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  async function handleDelete() {
    try {
      const { data } = await deleteMemberBand({ variables: { id: bandId } });

      if (data?.deleteMemberBand?.success) {
        enqueueSnackbar(data.deleteMemberBand.message || "Membership record deleted successfully", {
          variant: "default"
        });
        onClose();
      } else {
        enqueueSnackbar(data?.deleteMemberBand?.message || "Failed to delete membership record", { variant: "error" });
      }
    } catch (e) {
      const errorMessage = (e as Error).message || "An error occurred while deleting the membership record";
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: "error" });
    }
  }

  return (
    <Dialog
      className="p-6 w-[400px]"
      open={open}
      onClosed={onClose}
      headline="Delete Membership Record"
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="delete-membership-cancel-button">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleDelete}
            testId="delete-membership-confirm-button"
            disabled={loading}
            theme="error"
            icon="delete"
          >
            Delete
          </Button>
        </>
      }
    >
      <p className="text-body-large text-sys-brand-on-surface">
        Are you sure you want to delete this membership record? This cannot be undone.
      </p>
    </Dialog>
  );
}
